/**
 * crmb.js — Global JS for the crmb. Shopify theme
 *
 * Modules
 * ───────
 *  1.  Dependency boot (waits for GSAP)
 *  2.  Custom cursor
 *  3.  Hero title reveal
 *  4.  Scroll reveals (IntersectionObserver)
 *  5.  Horizontal trail (GSAP ScrollTrigger pin)
 *  6.  Filter tabs
 *  7.  Add to cart (Shopify Ajax API)
 *  8.  Cart count badge
 *  9.  Magnetic buttons
 * 10.  Daily order limit
 */

(function () {
  'use strict';

  /* ====================================================================
     1. BOOT — wait for GSAP then kick everything off
  ==================================================================== */
  function waitForDeps(callback) {
    var attempts = 0, max = 60;
    (function check() {
      if (typeof gsap !== 'undefined') {
        callback();
      } else if (++attempts < max) {
        setTimeout(check, 50);
      } else {
        initWithoutGsap();
      }
    })();
  }

  /* ====================================================================
     2. CUSTOM CURSOR
  ==================================================================== */
  function initCursor() {
    var dot  = document.getElementById('crmbCursorDot');
    var ring = document.getElementById('crmbCursorRing');
    if (!dot || !ring) return;

    var mouse   = { x: window.innerWidth / 2,  y: window.innerHeight / 2 };
    var ringPos = { x: mouse.x, y: mouse.y };

    window.addEventListener('mousemove', function (e) {
      mouse.x = e.clientX;
      mouse.y = e.clientY;
      dot.style.transform = 'translate(' + mouse.x + 'px,' + mouse.y + 'px) translate(-50%,-50%)';
    });

    (function rafLoop() {
      ringPos.x += (mouse.x - ringPos.x) * 0.18;
      ringPos.y += (mouse.y - ringPos.y) * 0.18;
      ring.style.transform = 'translate(' + ringPos.x + 'px,' + ringPos.y + 'px) translate(-50%,-50%)';
      requestAnimationFrame(rafLoop);
    })();

    function attachHoverListeners(root) {
      (root || document).querySelectorAll('[data-hover]').forEach(function (el) {
        if (el._crmbHoverAttached) return;
        el._crmbHoverAttached = true;
        el.addEventListener('mouseenter', function () { ring.classList.add('crmb-hover'); });
        el.addEventListener('mouseleave', function () { ring.classList.remove('crmb-hover'); });
      });
    }
    attachHoverListeners(document);

    new MutationObserver(function (mutations) {
      mutations.forEach(function (m) {
        m.addedNodes.forEach(function (node) {
          if (node.nodeType === 1) attachHoverListeners(node);
        });
      });
    }).observe(document.body, { childList: true, subtree: true });
  }

  /* ====================================================================
     3. HERO TITLE REVEAL
  ==================================================================== */
  function initHeroReveal() {
    if (typeof gsap === 'undefined') return;
    var spans = document.querySelectorAll('#crmbHeroTitle .word > span');
    if (!spans.length) return;
    gsap.to(spans, { y: '0%', duration: 1.2, stagger: 0.1, ease: 'expo.out', delay: 0.2 });
  }

  /* ====================================================================
     4. SCROLL REVEALS (IntersectionObserver)
  ==================================================================== */
  function initScrollReveals() {
    var els = document.querySelectorAll('.reveal');
    if (!els.length) return;

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('in');
          io.unobserve(entry.target);
        }
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -60px 0px' });

    els.forEach(function (el) { io.observe(el); });

    new MutationObserver(function () {
      document.querySelectorAll('.reveal:not(.in)').forEach(function (el) { io.observe(el); });
    }).observe(document.body, { childList: true, subtree: true });
  }

  /* ====================================================================
     5. HORIZONTAL TRAIL (GSAP ScrollTrigger)
  ==================================================================== */
  function initTrail() {
    if (typeof gsap === 'undefined' || typeof ScrollTrigger === 'undefined') return;
    gsap.registerPlugin(ScrollTrigger);

    var section = document.getElementById('crmbTrail');
    var track   = document.getElementById('crmbTrailTrack');
    if (!section || !track) return;

    function setupTrail() {
      var existing = ScrollTrigger.getById('crmb-trail');
      if (existing) { existing.kill(); gsap.set(track, { x: 0 }); }
      if (window.innerWidth <= 900) return;

      var scrollDist = track.scrollWidth - window.innerWidth + 80;
      gsap.to(track, {
        x: -scrollDist,
        ease: 'none',
        scrollTrigger: {
          id: 'crmb-trail',
          trigger: section,
          start: 'top top',
          end: function () { return '+=' + (scrollDist + window.innerHeight); },
          scrub: 1,
          pin: '#crmbTrailWrap',
          invalidateOnRefresh: true
        }
      });
    }

    setupTrail();
    var resizeTimer;
    window.addEventListener('resize', function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(function () { setupTrail(); ScrollTrigger.refresh(); }, 200);
    });
  }

  /* ====================================================================
     6. FILTER TABS
  ==================================================================== */
  function initFilters() {
    var filterGroup = document.querySelector('.filters');
    if (!filterGroup) return;
    var buttons = filterGroup.querySelectorAll('button[data-filter]');
    if (!buttons.length) return;

    buttons.forEach(function (btn) {
      btn.addEventListener('click', function () {
        buttons.forEach(function (b) { b.classList.remove('active'); });
        btn.classList.add('active');

        var filter = btn.getAttribute('data-filter');
        document.querySelectorAll('.crmb-card[data-type]').forEach(function (card) {
          var show = filter === 'all' || card.getAttribute('data-type') === filter;
          card.style.display = show ? '' : 'none';
        });
      });
    });
  }

  /* ====================================================================
     7. ADD TO CART (Shopify Ajax API)
  ==================================================================== */
  function initAddToCart() {
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('.add-btn[data-variant-id]');
      if (!btn || btn.disabled) return;

      /* ── Daily limit check ── */
      if (window.CRMB && window.CRMB.dailySoldOut) {
        var orig = btn.textContent;
        btn.textContent = 'Finished for today';
        setTimeout(function () { btn.textContent = orig; }, 2000);
        return;
      }

      btn.disabled = true;

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ id: btn.getAttribute('data-variant-id'), quantity: 1 })
      })
        .then(function (res) {
          if (!res.ok) throw new Error('Cart error ' + res.status);
          return res.json();
        })
        .then(function () {
          var orig = btn.textContent;
          btn.textContent = 'Added ✓';
          btn.classList.add('added');
          fetchCartCount();
          setTimeout(function () {
            btn.textContent = orig;
            btn.classList.remove('added');
            btn.disabled = false;
          }, 1600);
        })
        .catch(function (err) {
          console.warn('[crmb.js] Add to cart failed:', err);
          btn.textContent = 'Try again';
          btn.disabled = false;
          setTimeout(function () { btn.textContent = 'Add'; }, 2000);
        });
    });
  }

  /* ====================================================================
     8. CART COUNT BADGE
  ==================================================================== */
  function fetchCartCount() {
    fetch('/cart.js', { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then(function (res) { return res.json(); })
      .then(function (cart) { updateCartBadge(cart.item_count || 0); })
      .catch(function () {});
  }

  function updateCartBadge(count) {
    var badge = document.getElementById('crmbBagCount');
    if (badge) badge.textContent = count;

    /* Also update any Shopify-native cart count bubbles */
    document.querySelectorAll('.cart-count-bubble, [data-cart-count]').forEach(function (el) {
      var inner = el.querySelector('span:last-child, [aria-hidden="true"]');
      if (inner) inner.textContent = count;
      else el.textContent = count;
    });

    var bagLink = document.getElementById('crmbBagLink');
    if (bagLink) bagLink.setAttribute('aria-label', 'Shopping bag, ' + count + ' item' + (count !== 1 ? 's' : ''));
  }

  /* ====================================================================
     9. MAGNETIC BUTTONS
  ==================================================================== */
  function initMagneticButtons() {
    if (typeof gsap === 'undefined') return;
    var selectors = ['.hero-cta', '.news-form button[type="submit"]', '.filters button'].join(', ');
    document.querySelectorAll(selectors).forEach(function (btn) {
      if (btn._crmbMagnetAttached) return;
      btn._crmbMagnetAttached = true;
      btn.addEventListener('mousemove', function (e) {
        var r = btn.getBoundingClientRect();
        gsap.to(btn, {
          x: (e.clientX - r.left - r.width  / 2) * 0.3,
          y: (e.clientY - r.top  - r.height / 2) * 0.3,
          duration: 0.4, ease: 'power2.out'
        });
      });
      btn.addEventListener('mouseleave', function () {
        gsap.to(btn, { x: 0, y: 0, duration: 0.6, ease: 'elastic.out(1,0.3)' });
      });
    });
  }

  /* ====================================================================
     10. DAILY ORDER LIMIT
  ==================================================================== */
  function initDailyLimit() {
    var limitEl = document.getElementById('crmbDailyLimit');
    if (!limitEl) return;

    var remaining = parseInt(limitEl.getAttribute('data-remaining'), 10) || 0;
    var soldOut   = limitEl.getAttribute('data-sold-out') === 'true';

    /* Expose globally so add-to-cart can check synchronously */
    window.CRMB = window.CRMB || {};
    window.CRMB.dailyRemaining = remaining;
    window.CRMB.dailySoldOut   = soldOut;

    if (soldOut) {
      disableAllAddButtons('Finished for today');
      var grid = document.querySelector('.crmb-shop-wrapper');
      if (grid) grid.classList.add('crmb-daily-sold-out');
    }
  }

  function disableAllAddButtons(label) {
    var text = label || 'Finished for today';
    document.querySelectorAll('.add-btn').forEach(function (btn) {
      btn.disabled = true;
      btn.textContent = text;
      btn.style.opacity = '0.45';
      btn.style.cursor  = 'not-allowed';
      btn.style.pointerEvents = 'none';
    });

    new MutationObserver(function () {
      document.querySelectorAll('.add-btn:not([disabled])').forEach(function (btn) {
        btn.disabled = true;
        btn.textContent = text;
        btn.style.opacity = '0.45';
        btn.style.pointerEvents = 'none';
      });
    }).observe(document.body, { childList: true, subtree: true });
  }

  /* ====================================================================
     11. NAV COLOR SWITCH
  ==================================================================== */
  function initNavColorSwitch() {
    var nav = document.querySelector('.crmb-nav');
    if (!nav) return;
    var darkSections = document.querySelectorAll('[data-nav-invert]');
    if (!darkSections.length) return;

    var activeCount = 0;
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        activeCount += entry.isIntersecting ? 1 : -1;
        nav.classList.toggle('crmb-nav--light', activeCount > 0);
      });
    }, { threshold: 0, rootMargin: '-44px 0px 0px 0px' });

    darkSections.forEach(function (el) { io.observe(el); });
  }

  /* ====================================================================
     REDUCED MOTION
  ==================================================================== */
  function applyReducedMotion() {
    if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    document.querySelectorAll('#crmbHeroTitle .word span').forEach(function (el) {
      el.style.transform = 'none';
    });
    if (typeof gsap !== 'undefined') gsap.globalTimeline.timeScale(100);
  }

  /* ====================================================================
     FULL INIT
  ==================================================================== */
  function init() {
    initCursor();
    initHeroReveal();
    initScrollReveals();
    initTrail();
    initFilters();
    initAddToCart();
    initMagneticButtons();
    initDailyLimit();
    initNavColorSwitch();
    fetchCartCount();
    applyReducedMotion();
  }

  /* Fallback when GSAP never loads */
  function initWithoutGsap() {
    document.querySelectorAll('#crmbHeroTitle .word > span').forEach(function (el) {
      el.style.transform = 'none';
      el.style.transition = 'none';
    });
    initCursor();
    initScrollReveals();
    initFilters();
    initAddToCart();
    initDailyLimit();
    initNavColorSwitch();
    fetchCartCount();
  }

  /* ====================================================================
     ENTRY POINT
  ==================================================================== */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { waitForDeps(init); });
  } else {
    waitForDeps(init);
  }

})();
